import { A } from '@solidjs/router'
import { type Component, createEffect, createMemo, createSignal, For, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import Tooltip from '~/components/Tooltip'
import type { SidebarDotColorType, SidebarMenuItemType, SidebarMenuPropsType, SidebarMenuSectionType, SidebarMenuSubItemType } from '~/types/SidebarMenu'

/* ─── dot color map ─── */
const dotColors: Record<SidebarDotColorType, string> = {
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  yellow: 'bg-yellow-400',
  cyan: 'bg-cyan-400'
}

/* ─── Sub-item (dot + label) ─── */
const SubItemRow: Component<{
  item: SidebarMenuSubItemType
  collapsed: boolean
  active: boolean
  onNavigate?: () => void
}> = props => {
  const dot = createMemo(() => dotColors[props.item.dotColor ?? 'purple'])

  const content = (
    <div
      class={twMerge(
        'flex items-center gap-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200',
        props.collapsed ? 'justify-center px-2' : 'ps-12 pe-4',
        props.active ? 'text-primary font-semibold bg-primary-soft' : 'text-foreground-subtle hover:text-foreground hover:bg-surface-hover'
      )}
      onClick={() => {
        props.item.onClick?.()
        props.onNavigate?.()
      }}
    >
      <span class={twMerge('w-3 h-3 rounded-full shrink-0 shadow-sm transition-transform duration-300', dot())} />
      <Show when={!props.collapsed}>
        <span class="text-[15px] truncate font-medium tracking-tight leading-none">{props.item.label}</span>
      </Show>
    </div>
  )

  if (props.item.href) {
    return (
      <A href={props.item.href} class="block mb-1 group/sub">
        {props.collapsed ? (
          <Tooltip content={props.item.label} placement="right" triggerClass="w-full">
            {content}
          </Tooltip>
        ) : (
          content
        )}
      </A>
    )
  }

  return (
    <div class="mb-1">
      {props.collapsed ? (
        <Tooltip content={props.item.label} placement="right" triggerClass="w-full">
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  )
}

/* ─── Main menu item row ─── */
const MenuItemRow: Component<{
  item: SidebarMenuItemType
  collapsed: boolean
  active: boolean
  expanded: boolean
  onToggle: () => void
  onNavigate?: () => void
  isActive?: (href: string) => boolean
}> = props => {
  const hasChildren = () => (props.item.children?.length ?? 0) > 0

  const handleClick = () => {
    if (hasChildren()) {
      props.onToggle()
    } else {
      props.item.onClick?.()
      props.onNavigate?.()
    }
  }

  const inner = (
    <div
      class={twMerge(
        'group relative flex items-center gap-3 rounded-lg cursor-pointer transition-colors duration-200',
        props.collapsed ? 'justify-center p-2.5' : 'px-3 py-2.5',
        props.active ? 'bg-primary-soft text-primary font-semibold' : 'text-foreground-subtle font-medium hover:bg-surface-hover hover:text-foreground'
      )}
      onClick={handleClick}
    >
      {/* Indicador de activo */}
      <Show when={props.active && !props.collapsed}>
        <span class="absolute inset-y-1.5 start-0 w-1 rounded-full bg-primary" />
      </Show>

      {/* Icon */}
      <Icon icon={props.item.icon} size={22} class={twMerge('shrink-0 transition-colors duration-200', props.active ? 'text-primary' : 'text-foreground-subtle group-hover:text-foreground')} />

      <Show when={!props.collapsed}>
        <span class="flex-1 text-[15px] truncate tracking-tight">{props.item.label}</span>
        <Show when={props.item.badge !== undefined}>
          <span
            class={twMerge(
              'min-w-5 h-5 flex items-center justify-center text-[11px] font-bold rounded-full px-1.5 transition-colors',
              props.active ? 'bg-primary text-white' : 'bg-surface-muted text-foreground-subtle'
            )}
          >
            {props.item.badge}
          </span>
        </Show>
        <Show when={hasChildren()}>
          <Icon
            icon="material-symbols-light:expand-less"
            size={16}
            class={twMerge('transition-transform duration-300', props.active ? 'text-primary' : 'text-foreground-subtle', !props.expanded && 'rotate-180')}
          />
        </Show>
      </Show>
      {/* Collapsed badge */}
      <Show when={props.collapsed && props.item.badge !== undefined}>
        <span class="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full bg-primary text-white shadow-sm ring-2 ring-surface px-1">
          {props.item.badge}
        </span>
      </Show>
    </div>
  )

  const wrappedInner =
    props.item.href && !hasChildren() ? (
      <A href={props.item.href} class="block mb-1 last:mb-0">
        {props.collapsed ? (
          <Tooltip content={props.item.label} placement="right">
            {inner}
          </Tooltip>
        ) : (
          inner
        )}
      </A>
    ) : (
      <div class="mb-1 last:mb-0">
        {props.collapsed ? (
          <Tooltip content={props.item.label} placement="right">
            {inner}
          </Tooltip>
        ) : (
          inner
        )}
      </div>
    )

  return (
    <div class="relative">
      {wrappedInner}
      {/* Sub-items */}
      <Show when={hasChildren() && (props.expanded || props.collapsed)}>
        <div class={twMerge('overflow-hidden transition-all duration-300', props.expanded ? 'max-h-[1000px] mb-4' : 'max-h-0')}>
          <For each={props.item.children}>
            {sub => <SubItemRow item={sub} collapsed={props.collapsed} active={sub.href ? (props.isActive?.(sub.href) ?? false) : false} onNavigate={props.onNavigate} />}
          </For>
        </div>
      </Show>
    </div>
  )
}

/* ─── Section header ─── */
const SectionHeader: Component<{ title: string; collapsed: boolean; isFirst?: boolean }> = props => (
  <Show when={!props.collapsed} fallback={<div class="mx-auto my-3 h-px w-6 rounded-full bg-border" />}>
    <div class={twMerge('mb-2 px-1', props.isFirst ? 'mt-2' : 'mt-6')}>
      <span class="text-xs font-semibold text-foreground-subtle">{props.title}</span>
    </div>
  </Show>
)

/* ─── Main SidebarMenu component ─── */
const SidebarMenu: Component<SidebarMenuPropsType> = props => {
  const collapsed = createMemo(() => props.collapsed ?? false)
  const [expandedKeys, setExpandedKeys] = createSignal<string[]>([])

  const toggleExpanded = (key: string) => {
    setExpandedKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  const defaultIsActive = (href: string) => {
    if (typeof window === 'undefined') return false
    const pathname = window.location.pathname
    if (href === '/') return pathname === '/'
    return pathname === href
  }

  const checkActive = (href: string) => (props.isActive ?? defaultIsActive)(href)

  /**
   * Siempre a través de `checkActive`, que lee la ruta del router (una señal).
   * Mirar `window.location.pathname` a mano dejaba el resaltado pegado: al entrar
   * en el padre (p.ej. /admin/crm) la comprobación devolvía `true` sin depender de
   * ninguna señal, así que Solid no la volvía a evaluar y el grupo seguía marcado
   * después de navegar a otra sección.
   */
  const isItemActive = (item: SidebarMenuItemType): boolean => {
    if (item.children?.length) {
      if (item.href && checkActive(item.href)) return true
      return item.children.some(sub => sub.href && checkActive(sub.href))
    }
    if (item.href) return checkActive(item.href)
    return false
  }

  /**
   * Un grupo con un sub-ítem activo se abre solo. Sin esto, al entrar por enlace
   * directo el menú deja la sección cerrada y no se ve dónde estás. Lo que el
   * usuario haya abierto a mano se respeta: sólo se añaden claves.
   */
  createEffect(() => {
    const active = props.sections
      .flatMap(section => section.items)
      .filter(item => item.children?.some(sub => sub.href && checkActive(sub.href)))
      .map(item => item.key)

    if (active.length === 0) return
    setExpandedKeys(prev => (active.every(key => prev.includes(key)) ? prev : [...new Set([...prev, ...active])]))
  })

  return (
    <div class={twMerge('flex flex-col h-full bg-surface text-foreground transition-all duration-300 select-none border-e border-border', collapsed() ? 'w-20' : 'w-full', props.class)}>
      {/* Header */}
      <Show when={props.header || props.onToggleCollapse}>
        <div class={twMerge('flex items-center shrink-0 p-4 pb-1 min-h-18', collapsed() ? 'flex-col gap-4' : 'justify-between')}>
          {props.header}
          <Show when={props.onToggleCollapse}>
            <button
              type="button"
              onClick={props.onToggleCollapse}
              class="w-8 h-8 flex items-center justify-center rounded-xl bg-surface-muted text-foreground-subtle hover:text-primary hover:shadow-sm transition-all duration-300 border border-border"
              aria-label={collapsed() ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon icon={collapsed() ? 'material-symbols-light:chevron-right' : 'material-symbols-light:chevron-left'} size={18} />
            </button>
          </Show>
        </div>
      </Show>

      {/* Navigation */}
      <nav class="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 scrollbar-thin scrollbar-thumb-border">
        <For each={props.sections}>
          {(section: SidebarMenuSectionType) => (
            <>
              <SectionHeader title={section.title} collapsed={collapsed()} isFirst={section === props.sections[0]} />
              <For each={section.items}>
                {(item: SidebarMenuItemType) => (
                  <MenuItemRow
                    item={item}
                    collapsed={collapsed()}
                    active={isItemActive(item)}
                    expanded={expandedKeys().includes(item.key)}
                    onToggle={() => toggleExpanded(item.key)}
                    onNavigate={props.onNavigate}
                    isActive={checkActive}
                  />
                )}
              </For>
            </>
          )}
        </For>
      </nav>

      {/* Footer */}
      <Show when={props.footer}>
        <div class="border-t border-border/60 p-4">{props.footer}</div>
      </Show>
    </div>
  )
}

export default SidebarMenu
